import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, apiClient } from '../store/authStore';
import { CheckCircle, Spinner, ArrowLeft } from '@phosphor-icons/react';

const C = {
  bg:       '#0a0a0f',
  surface:  '#13131f',
  surfaceHi:'#181828',
  border:   'rgba(124,58,237,0.2)',
  borderHi: 'rgba(124,58,237,0.5)',
  violet:   '#7c3aed',
  blue:     '#3b82f6',
  text:     '#f1f5f9',
  muted:    '#94a3b8',
  faint:    '#475569',
  gradient: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  shadow:   '0 4px 24px rgba(124,58,237,0.1)',
  shadowHi: '0 0 40px rgba(124,58,237,0.2)',
  font:     "'Inter', system-ui, -apple-system, sans-serif",
};

const STATS = [
  { value:'50K+',  label:'Sites analyzed' },
  { value:'2.5M',  label:'Issues fixed' },
  { value:'98%',   label:'Satisfaction rate' },
  { value:'< 30s', label:'Analysis time' },
];

const FAQ = [
  { q:'Can I cancel anytime?',    a:'Yes — cancel from your account settings. You keep access until the end of your billing period.' },
  { q:'What payment methods?',    a:'All major credit cards via Stripe. Secure, encrypted, no card details stored on our servers.' },
  { q:'Can I switch plans?',      a:'Yes — upgrade or downgrade at any time. Changes take effect immediately with prorated billing.' },
  { q:'Is there a free trial?',   a:'The Free plan is yours forever. No time limit, no credit card required to get started.' },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuthStore();
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'https://seodash360.com'}/api/subscriptions/plans`)
      .then(r => r.json())
      .then(data => setPlans(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [searchParams]);

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (planId === 'free') { navigate('/dashboard'); return; }
    setCheckoutLoading(planId);
    try {
      const data = await apiClient.post('/stripe/checkout', { plan_id: planId, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
      alert('Failed to start checkout. Please try again.');
    } finally { setCheckoutLoading(null); }
  };

  const currentPlan = user?.plan || 'free';

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: C.font, color: C.text }}>

      {/* Navbar */}
      <nav style={{ background: 'rgba(10,10,15,0.9)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 28px', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
            <div style={{ width: 30, height: 30, background: C.gradient, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>✦</div>
            <span style={{ fontWeight: 800, fontSize: 15, color: C.text, letterSpacing: '-0.4px' }}>SEO Dash <span style={{ color: C.violet }}>360</span></span>
          </Link>
          {isAuthenticated
            ? <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 500, color: C.muted, textDecoration: 'none', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHi; e.currentTarget.style.color = C.text; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}>
                <ArrowLeft style={{ width: 13, height: 13 }} /> Dashboard
              </Link>
            : <Link to="/login" style={{ background: C.gradient, color: '#fff', padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none', boxShadow: '0 2px 12px rgba(124,58,237,0.3)' }}>Sign in</Link>
          }
        </div>
      </nav>

      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '72px 28px 56px', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 600, height: 300, background: 'radial-gradient(ellipse at center, rgba(124,58,237,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <p style={{ fontSize: 11, fontWeight: 700, color: C.violet, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 12px' }}>Pricing</p>
        <h1 style={{ fontSize: 42, fontWeight: 900, margin: '0 0 14px', letterSpacing: '-1.2px', color: C.text }}>Simple, transparent pricing</h1>
        <p style={{ fontSize: 16, color: C.muted, margin: 0, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
          Start free, upgrade when you need more. No hidden fees.
        </p>
      </section>

      {/* Stats bar */}
      <section style={{ background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, marginBottom: 56 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)' }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '24px 16px', borderRight: i < 3 ? `1px solid ${C.border}` : 'none' }}>
              <p style={{ fontSize: 28, fontWeight: 900, margin: 0, background: C.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{s.value}</p>
              <p style={{ fontSize: 11, color: C.muted, margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <main style={{ maxWidth: 1000, margin: '0 auto', padding: '0 28px 80px' }}>

        {/* Plans grid */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 56 }}>
            <Spinner style={{ width: 28, height: 28, color: C.violet }} className="animate-spin" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, alignItems: 'start', marginBottom: 64 }}>
            {plans.map(plan => {
              const isCurrent = currentPlan === plan.plan_id;
              const isPro = plan.plan_id === 'pro';
              return (
                <div key={plan.plan_id}
                  data-testid={`plan-${plan.plan_id}`}
                  style={{
                    background:   isPro ? C.surfaceHi : C.surface,
                    border:       isPro ? `2px solid ${C.violet}` : `1px solid ${C.border}`,
                    borderRadius: 20, padding: '32px 26px', position: 'relative',
                    boxShadow:    isPro ? C.shadowHi : C.shadow,
                    transition:   'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={e => { if (!isPro) { e.currentTarget.style.borderColor = C.borderHi; e.currentTarget.style.boxShadow = C.shadowHi; } }}
                  onMouseLeave={e => { if (!isPro) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = C.shadow; } }}>

                  {isPro && (
                    <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: C.gradient, color: '#fff', fontSize: 10, fontWeight: 800, padding: '4px 16px', borderRadius: 99, letterSpacing: '0.08em', whiteSpace: 'nowrap', boxShadow: '0 2px 12px rgba(124,58,237,0.4)' }}>
                      MOST POPULAR
                    </div>
                  )}

                  <div style={{ marginBottom: 24 }}>
                    <p style={{ fontSize: 11, fontWeight: 800, color: isPro ? C.violet : C.faint, margin: '0 0 10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{plan.name}</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                      <span style={{ fontSize: 42, fontWeight: 900, color: C.text, letterSpacing: '-1.5px', lineHeight: 1 }}>
                        {plan.price === 0 ? 'Free' : `€${plan.price}`}
                      </span>
                      {plan.price > 0 && <span style={{ fontSize: 14, color: C.faint }}>/month</span>}
                    </div>
                  </div>

                  <button
                    onClick={() => handleSubscribe(plan.plan_id)}
                    disabled={isCurrent || checkoutLoading === plan.plan_id}
                    data-testid={`subscribe-${plan.plan_id}`}
                    style={{
                      width: '100%', padding: '12px 0', borderRadius: 10, fontSize: 14, fontWeight: 700,
                      cursor: isCurrent ? 'not-allowed' : 'pointer', border: 'none',
                      background: isCurrent ? 'rgba(255,255,255,0.06)' : C.gradient,
                      color: isCurrent ? C.faint : '#fff',
                      opacity: isCurrent ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                      marginBottom: 24, fontFamily: C.font,
                      boxShadow: (!isCurrent && isPro) ? '0 4px 20px rgba(124,58,237,0.35)' : 'none',
                    }}>
                    {checkoutLoading === plan.plan_id
                      ? <Spinner style={{ width: 14, height: 14 }} className="animate-spin" />
                      : isCurrent ? 'Current plan'
                      : plan.price === 0 ? 'Get started free'
                      : 'Subscribe →'}
                  </button>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(plan.features || []).map((feat, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <CheckCircle weight="fill" style={{ width: 15, height: 15, color: isPro ? C.violet : '#10b981', flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ */}
        <div style={{ marginBottom: 40 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '0 0 24px', letterSpacing: '-0.5px' }}>Frequently asked questions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {FAQ.map((faq, i) => (
              <div key={i}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '20px 22px', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHi}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>{faq.q}</p>
                <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.65 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: C.faint, margin: 0 }}>
            Questions?{' '}
            <a href="mailto:support@seodash360.com" style={{ color: C.violet, textDecoration: 'none', fontWeight: 600 }}>
              support@seodash360.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
